import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import buttonImage from '../buttonimage.webp';
import ClipLoader from 'react-spinners/ClipLoader';
import AnimatedHelper from './AnimatedHelper';

const HealingFormulas = ({ goBack, goHome, navigateTo, setHelperData }) => {
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [selectedAnswer, setSelectedAnswer] = useState('');
    const [questionHistory, setQuestionHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showHelper, setShowHelper] = useState(false);
    const [initialQuestionId, setInitialQuestionId] = useState(null);
    const [helperQuestion, setHelperQuestion] = useState(null);
    const [helperButtons, setHelperButtons] = useState([]);
    const [showRestrictionModal, setShowRestrictionModal] = useState(null); // New state for modal
    // provide setter only for debug logging calls elsewhere without creating an unused variable
    // eslint-disable-next-line no-unused-vars
    const [, setDebugLog] = useState([]);

    useEffect(() => {
        const checkSession = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                setShowRestrictionModal({ message: 'Please log in to start a session.', redirect: 'login' });
                return false;
            }

            const { data: profile, error: profileError } = await supabase
                .schema('api')
                .from('user_profiles')
                .select('membership_type, session_complete, session_start_date, created_at, next_membership_start')
                .eq('id', user.id)
                .single();

            if (profileError) {
                console.error('Error fetching profile:', profileError.message);
                setShowRestrictionModal({ message: 'Failed to load user profile.', redirect: 'login' });
                return false;
            }

            console.log('Debug: Profile data:', {
                membership_type: profile.membership_type,
                session_complete: profile.session_complete,
                session_start_date: profile.session_start_date,
                created_at: profile.created_at,
                next_membership_start: profile.next_membership_start
            });

            if (profile.membership_type === 'Free Subscription') {
                const now = new Date();
                console.log('Debug: Current UTC time:', now.toISOString());

                // 24-hour timeout check for incomplete sessions
                if (profile.session_start_date && !profile.session_complete) {
                    const sessionStart = new Date(profile.session_start_date + 'Z');
                    const hoursSinceStart = (now - sessionStart) / (1000 * 60 * 60);
                    console.log('Debug: Session timeout check:', {
                        session_start_date: profile.session_start_date,
                        parsed_session_start: sessionStart.toISOString(),
                        hoursSinceStart: hoursSinceStart.toFixed(2),
                        is_timeout: hoursSinceStart > 24
                    });
                    if (hoursSinceStart > 24) {
                        const baseDate = profile.next_membership_start && new Date(profile.next_membership_start) <= now
                            ? new Date(Math.max(new Date(profile.created_at), new Date(profile.next_membership_start)))
                            : new Date(profile.created_at);
                        const daysSinceBase = Math.floor((now - baseDate) / (24 * 60 * 60 * 1000));
                        const monthsSinceBase = Math.floor(daysSinceBase / 30);
                        const resetDate = new Date(baseDate);
                        resetDate.setDate(resetDate.getDate() + (monthsSinceBase + 1) * 30);
                        setShowRestrictionModal({
                            message: `You've used your free monthly session. Upgrade to Monthly or Yearly for unlimited access. Your next free session resets on ${resetDate.toLocaleDateString()}.`,
                            redirect: 'back'
                        });
                        return false;
                    }
                }

                // Monthly session limit check
                const baseDate = profile.next_membership_start && new Date(profile.next_membership_start) <= now
                    ? new Date(Math.max(new Date(profile.created_at), new Date(profile.next_membership_start)))
                    : new Date(profile.created_at);
                const daysSinceBase = Math.floor((now - baseDate) / (24 * 60 * 60 * 1000));
                const monthsSinceBase = Math.floor(daysSinceBase / 30);
                const resetDate = new Date(baseDate);
                resetDate.setDate(resetDate.getDate() + (monthsSinceBase + 1) * 30);

                if (profile.session_complete && daysSinceBase < (monthsSinceBase + 1) * 30) {
                    console.log('Debug: Monthly limit reached, reset date:', resetDate.toLocaleDateString());
                    setShowRestrictionModal({
                        message: `You've used your free monthly session. Upgrade to Monthly or Yearly for unlimited access. Your next free session resets on ${resetDate.toLocaleDateString()}.`,
                        redirect: 'back'
                    });
                    return false;
                }

                // Update session_start_date for new sessions
                if (!profile.session_complete && !profile.session_start_date) {
                    try {
                        const { error: updateError } = await supabase
                            .schema('api')
                            .from('user_profiles')
                            .update({
                                session_start_date: new Date().toISOString(),
                            })
                            .eq('id', user.id);

                        if (updateError) {
                            console.error('Error updating session_start_date:', updateError.message);
                            setShowRestrictionModal({ message: 'Failed to start the session. Please try again.', redirect: 'back' });
                            return false;
                        }

                        console.log('Session start date updated successfully.');
                    } catch (err) {
                        console.error('Unexpected error updating session_start_date:', err);
                        setShowRestrictionModal({ message: 'An unexpected error occurred while starting the session.', redirect: 'back' });
                        return false;
                    }
                }
            }

            return true;
        };

        const fetchInitial = async () => {
            console.log('Starting question fetch at:', new Date().toISOString());
            setLoading(true);
            setError(null);

            try {
                const resumeQuestionId = localStorage.getItem('resumeQuestionId');
                const isResumeSession = resumeQuestionId && resumeQuestionId !== 'null';

                if (isResumeSession) {
                    console.log('Attempting to resume with ID:', resumeQuestionId);
                    try {
                        const { data: savedQuestion, error: resumeError } = await supabase
                            .from('questions2')
                            .select('*')
                            .eq('id', resumeQuestionId)
                            .single();

                        if (resumeError) {
                            console.error('Error fetching resumed question:', resumeError.message);
                            setShowRestrictionModal({ message: 'Failed to load the resumed question: ' + resumeError.message, redirect: 'back' });
                            return;
                        } else if (!savedQuestion) {
                            console.error('No question found for the saved session.');
                            setShowRestrictionModal({ message: 'No question found for the saved session.', redirect: 'back' });
                            return;
                        } else {
                            setCurrentQuestion(savedQuestion);
                            fetchAnswers(savedQuestion.id);
                            console.log('Loaded saved question:', savedQuestion);
                            return;
                        }
                    } catch (err) {
                        console.error('Unexpected error fetching question:', err.message);
                        setShowRestrictionModal({ message: 'An unexpected error occurred: ' + err.message, redirect: 'back' });
                        return;
                    } finally {
                        localStorage.removeItem('resumeQuestionId');
                        console.log('Cleared resume ID from localStorage');
                    }
                }

                console.log('Fetching first question from questions2');
                const { data: question, error } = await supabase
                    .from('questions2')
                    .select('*')
                    .eq('is_first', true);

                if (error) {
                    console.error('Error fetching first question:', error.message);
                    setShowRestrictionModal({ message: 'Failed to load the first question.', redirect: 'back' });
                    return;
                }

                if (!question || question.length === 0) {
                    console.error('No first question found in questions2');
                    setShowRestrictionModal({ message: 'No first question available.', redirect: 'back' });
                    return;
                }

                setCurrentQuestion(question[0]);
                fetchAnswers(question[0].id);
                console.log('Loaded first question:', question[0]);
            } catch (err) {
                console.error('Unexpected error fetching question:', err.message);
                setShowRestrictionModal({ message: 'An unexpected error occurred.', redirect: 'back' });
            } finally {
                setLoading(false);
                console.log('Fetch process completed');
            }
        };

        const initialize = async () => {
            const canProceed = await checkSession();
            if (!canProceed) return;
            await fetchInitial();
        };
        initialize();
    }, [navigateTo, goBack]);

    const fetchAnswers = async (questionId) => {
        console.log('Fetching answers for question ID:', questionId);
        try {
            const { data: answers, error } = await supabase
                .from('answers2')
                .select('*')
                .eq('question2_id', questionId);

            if (error) {
                console.error('Error fetching answers:', error.message);
                setShowRestrictionModal({ message: 'Failed to load answers.', redirect: 'back' });
                return;
            }

            setAnswers(answers || []);
            console.log('Fetched answers:', answers);
            setDebugLog(prev => [...prev, 'Answers fetched successfully']);
        } catch (err) {
            console.error('Unexpected error fetching answers:', err.message);
            setDebugLog(prev => [...prev, `Unexpected answer error: ${err.message}`]);
            setShowRestrictionModal({ message: 'An unexpected error occurred while fetching answers.', redirect: 'back' });
        }
    };

    const fetchHelperQuestion = async (questionId = null) => {
        console.log('Fetching helper question from questions3', questionId ? `for ID: ${questionId}` : 'for first question');
        try {
            const query = supabase.from('questions3').select('*');
            if (questionId) {
                query.eq('id', questionId);
            } else {
                query.eq('is_first', true);
            }
            const { data: question, error } = await query.single();
            if (error) {
                console.error('Error fetching helper question:', error.message);
                setShowRestrictionModal({ message: 'Failed to load helper question.', redirect: 'back' });
                return null;
            }
            console.log('Fetched helper question:', question);
            setHelperQuestion(question);
            setDebugLog(prev => [...prev, 'Helper question fetched']);
            return question;
        } catch (err) {
            console.error('Unexpected error fetching helper question:', err.message);
            setDebugLog(prev => [...prev, `Unexpected helper error: ${err.message}`]);
            setShowRestrictionModal({ message: 'An unexpected error occurred while fetching helper question.', redirect: 'back' });
            return null;
        }
    };

    const handleAnswerSubmit = async (answerText) => {
        console.log('Handling answer submit:', answerText);
        if (!answerText) return;
        const answer = answers.find(a => a.text === answerText);
        if (!answer) {
            console.error('Answer not found:', answerText);
            setDebugLog(prev => [...prev, 'Answer not found']);
            setShowRestrictionModal({ message: 'Invalid answer selected.', redirect: 'back' });
            return;
        }

        setShowHelper(false);

        // --- FIX: Pass helper_question_id to AnimatedHelper ---
        if (answer.component) {
            if (answer.component.toLowerCase() === 'animatedhelper') {
                setShowHelper(true);
                // Use helper_question_id from answers2 row
                let helperQid = answer.helper_question_id;
                let firstQuestion = null;
                if (helperQid) {
                    firstQuestion = await fetchHelperQuestion(helperQid);
                } else {
                    firstQuestion = await fetchHelperQuestion();
                }
                if (firstQuestion) {
                    setInitialQuestionId(firstQuestion.id);
                    setHelperQuestion(firstQuestion);
                    setHelperButtons([]);
                    if (setHelperData) {
                        setHelperData({
                            isActive: true,
                            initialQuestionId: firstQuestion.id,
                            answerHelperQuestionId: helperQid || null, // <-- pass this prop
                            question: firstQuestion,
                            buttons: []
                        });
                    }
                    console.log('Activated AnimatedHelper with ID:', firstQuestion.id);
                    setDebugLog(prev => [...prev, 'AnimatedHelper activated']);
                }
            } else {
                if (setHelperData) setHelperData(null);
                navigateTo(answer.component);
            }
            return;
        }

        if (answer.next_question2_id) {
            try {
                console.log('Selected answer:', answer);
                console.log('Next question ID from answer:', answer.next_question2_id);

                const { data: nextQuestion, error } = await supabase
                    .from('questions2')
                    .select('*')
                    .eq('id', answer.next_question2_id);

                if (error) {
                    console.error('Error fetching next question:', error.message);
                    setDebugLog(prev => [...prev, `Next question error: ${error.message}`]);
                    setShowRestrictionModal({ message: 'Failed to load the next question.', redirect: 'back' });
                    return;
                }

                const nextQuestionObject = Array.isArray(nextQuestion) ? nextQuestion[0] : nextQuestion;
                console.log('Fetched next question:', nextQuestionObject);

                if (!nextQuestionObject?.id) {
                    console.error('Invalid next question ID:', nextQuestionObject);
                    setDebugLog(prev => [...prev, 'Invalid next question']);
                    setShowRestrictionModal({ message: 'Invalid next question.', redirect: 'back' });
                    return;
                }

                setQuestionHistory([...questionHistory, currentQuestion]);
                setCurrentQuestion(nextQuestionObject);
                fetchAnswers(nextQuestionObject.id);
                setSelectedAnswer('');
                console.log('Moved to next question:', nextQuestionObject.id);
                setDebugLog(prev => [...prev, 'Moved to next question']);
            } catch (err) {
                console.error('Unexpected error fetching next question:', err.message);
                setDebugLog(prev => [...prev, `Unexpected next error: ${err.message}`]);
                setShowRestrictionModal({ message: 'An unexpected error occurred.', redirect: 'back' });
            }
        } else {
            const { data: { user } } = await supabase.auth.getUser();
            const { data: profile, error: profileError } = await supabase
                .schema('api')
                .from('user_profiles')
                .select('session_count')
                .eq('id', user.id)
                .single();

            if (profileError) {
                console.error('Error fetching profile:', profileError.message);
                setShowRestrictionModal({ message: 'Failed to load user profile.', redirect: 'back' });
                return;
            }

            await supabase
                .schema('api')
                .from('user_profiles')
                .update({
                    session_complete: true,
                    session_count: profile.session_count + 1,
                    last_session_date: new Date().toISOString(),
                    completion_method: 'completed',
                })
                .eq('id', user.id);
            setShowRestrictionModal({ message: 'End of session!', redirect: 'endofSession' });
            console.log('End of session reached');
            setDebugLog(prev => [...prev, 'End of session']);
        }
    };

    const handleBack = () => {
        console.log('Debug: Handling back navigation');
        if (questionHistory.length > 0) {
            const newHistory = [...questionHistory];
            const previousQuestion = newHistory.pop();
            setCurrentQuestion(previousQuestion);
            fetchAnswers(previousQuestion.id);
            setQuestionHistory(newHistory);
            setSelectedAnswer('');
            setShowHelper(false);
            if (setHelperData) setHelperData(null);
            console.log('Returned to previous question:', previousQuestion.id);
            setDebugLog(prev => [...prev, 'Returned to previous']);
        } else {
            console.log('Debug: Navigating back');
            goBack();
            setShowHelper(false);
            if (setHelperData) setHelperData(null);
            setDebugLog(prev => [...prev, 'Returned to home']);
        }
    };

    const handleSave = async () => {
        console.log('Saving progress');
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                console.error('No authenticated user:', userError?.message || 'User object is null');
                alert('User not logged in! Please log in to save progress.');
                return;
            }

            console.log('User ID:', user.id);

            const progress = {
                user_id: user.id,
                current_question_id: currentQuestion?.id || null,
                current_component: 'healingFormulas',
                animated_helper: 'no', // always set to 'no'
                timestamp: new Date().toISOString(),
            };

            const { error } = await supabase
                .schema('api')
                .from('user_progress')
                .upsert(progress, { onConflict: ['user_id'] });

            if (error) {
                console.error('Error saving progress:', error.message);
                alert('Failed to save progress: ' + error.message);
                return;
            }

            console.log('Progress saved successfully:', progress);
            alert('Session progress has been saved successfully!');
        } catch (err) {
            console.error('Unexpected error saving progress:', err.message);
            alert('An unexpected error occurred while saving progress.');
        }
    };

    const handleHelperClick = async (path, nextQuestionId) => {
        console.log('Debug: Handling helper click, navigating to:', path);
        setShowHelper(false);
        navigateTo(path);
        if (nextQuestionId) {
            const question = await fetchHelperQuestion(nextQuestionId);
            if (question) {
                setHelperQuestion(question);
                setInitialQuestionId(nextQuestionId);
                setHelperButtons([]);
                if (setHelperData) {
                    setHelperData({ isActive: true, initialQuestionId: nextQuestionId, question, buttons: [] });
                }
                console.log('Updated helper question:', question.id);
                setDebugLog(prev => [...prev, 'Helper question updated']);
                setShowHelper(true);
            }
        }
    };

    useEffect(() => {
        const debugSupabase = async () => {
            console.log('Debugging Supabase client at:', new Date().toISOString());
            try {
                const { data: testData, error: testError } = await supabase.from('questions2').select('*').limit(1);
                if (testError) {
                    console.error('Error testing Supabase client:', testError.message);
                    setDebugLog(prev => [...prev, `Test error: ${testError.message}`]);
                    setShowRestrictionModal({ message: 'Error testing Supabase client: ' + testError.message, redirect: 'back' });
                } else {
                    console.log('Test query result (questions2):', testData);
                    setDebugLog(prev => [...prev, 'Test query succeeded']);
                }

                const { data: { user }, error: userError } = await supabase.auth.getUser();
                if (userError) {
                    console.error('Error fetching user:', userError.message);
                    setDebugLog(prev => [...prev, `User error: ${userError.message}`]);
                    setShowRestrictionModal({ message: 'Error fetching user: ' + userError.message, redirect: 'back' });
                    return;
                }
                if (user) {
                    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
                    if (sessionError) {
                        console.error('Error fetching session:', sessionError.message);
                        setDebugLog(prev => [...prev, `Session error: ${sessionError.message}`]);
                        setShowRestrictionModal({ message: 'Error fetching session: ' + sessionError.message, redirect: 'back' });
                        return;
                    }
                    if (sessionData?.session?.access_token) {
                        const decodedToken = JSON.parse(atob(sessionData.session.access_token.split('.')[1]));
                        console.log('Decoded JWT:', decodedToken);
                        setDebugLog(prev => [...prev, 'JWT decoded']);
                    } else {
                        console.error('No access token found');
                        setDebugLog(prev => [...prev, 'No access token']);
                        setShowRestrictionModal({ message: 'No access token found.', redirect: 'back' });
                    }
                } else {
                    console.error('No authenticated user found');
                    setDebugLog(prev => [...prev, 'No authenticated user']);
                    setShowRestrictionModal({ message: 'No authenticated user found.', redirect: 'back' });
                }
            } catch (err) {
                console.error('Unexpected error during debugging:', err.message);
                setDebugLog(prev => [...prev, `Debug error: ${err.message}`]);
                setShowRestrictionModal({ message: 'Unexpected error during debugging: ' + err.message, redirect: 'back' });
            }
        };
        debugSupabase();
    }, []);

    // Render modal if restriction or error occurs
    if (showRestrictionModal) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    maxWidth: window.innerWidth <= 600 ? '90vw' : '400px',
                    width: '100%',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid #4B5563',
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: window.innerWidth <= 600 ? '16px' : '18px',
                    color: '#4B5563',
                }}>
                    <p style={{ marginBottom: '16px', fontWeight: 'bold' }}>{showRestrictionModal.message}</p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        {showRestrictionModal.redirect === 'back' && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: window.innerWidth <= 600 ? '45vw' : '140px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking Back button');
                                    setShowRestrictionModal(null);
                                    goBack();
                                }}
                            >
                                Back
                            </button>
                        )}
                        {(showRestrictionModal.redirect === 'back' || showRestrictionModal.redirect === null) && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: window.innerWidth <= 600 ? '45vw' : '140px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking My Account button, navigating to myAccount');
                                    setShowRestrictionModal(null);
                                    navigateTo('myAccount');
                                }}
                            >
                                My Account
                            </button>
                        )}
                        {showRestrictionModal.redirect && showRestrictionModal.redirect !== 'back' && showRestrictionModal.redirect !== null && (
                            <button
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: window.innerWidth <= 600 ? '7px 0' : '10px 20px',
                                    width: window.innerWidth <= 600 ? '45vw' : '140px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: window.innerWidth <= 600 ? '15px' : '16px',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    console.log('Debug: Clicking OK button, navigating to', showRestrictionModal.redirect);
                                    setShowRestrictionModal(null);
                                    navigateTo(showRestrictionModal.redirect);
                                }}
                            >
                                OK
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>
                {`
                @media (max-width: 600px) {
                    .hf-card-container {
                        max-width: 98vw !important;
                        width: 98vw !important;
                        margin: 8px auto !important;
                        border-radius: 6px !important;
                    }
                    .hf-image {
                        width: 98vw !important;
                        height: 120px !important;
                        border-radius: 6px 6px 0 0 !important;
                    }
                    .hf-card-content {
                        width: 98vw !important;
                        height: auto !important;
                        min-height: 120px;
                        padding: 6px !important;
                        border-radius: 0 0 6px 6px !important;
                    }
                    .hf-select {
                        width: 90vw !important;
                        font-size: 16px !important;
                    }
                    .hf-nav-btns {
                        flex-direction: row !important;
                        gap: 6px !important;
                        margin-top: 24px !important;
                    }
                    .hf-btn {
                        width: 32vw !important;
                        min-width: 0 !important;
                        max-width: 100px !important;
                        padding: 10px 0 !important;
                        font-size: 16px !important;
                    }
                }
                `}
            </style>
            <div style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                zIndex: 1000,
            }}>
                {loading ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                    }}>
                        <ClipLoader size={50} color="#4B5563" />
                    </div>
                ) : (
                    <div style={{ position: 'relative', zIndex: 1000 }}>
                        <div
                            className="hf-card-container"
                            style={{
                                maxWidth: '560px',
                                border: '2px solid #4B5563',
                                borderRadius: '8px',
                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                margin: '16px auto',
                                position: 'relative',
                                zIndex: 1000,
                            }}>
                            {error ? (
                                <p style={{
                                    marginTop: '8px',
                                    color: '#4B5563',
                                    textAlign: 'center',
                                    maxWidth: '100%',
                                    wordWrap: 'break-word',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                }}>
                                    {error}
                                </p>
                            ) : !currentQuestion ? (
                                <p style={{
                                    marginTop: '8px',
                                    color: '#4B5563',
                                    textAlign: 'center',
                                    maxWidth: '100%',
                                    wordWrap: 'break-word',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                }}>
                                    No question found.
                                </p>
                            ) : (
                                <>
                                    <div
                                        className="hf-image"
                                        style={{
                                            width: '542px',
                                            height: '192px',
                                            backgroundImage: currentQuestion.image ? `url(/images/${currentQuestion.image})` : 'none',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'bottom',
                                            border: '2px solid #4B5563',
                                        }}
                                    ></div>
                                    <div
                                        className="hf-card-content"
                                        style={{
                                            width: '538px',
                                            height: '188px',
                                            padding: '8px',
                                            backgroundImage: `url(${cardBackgroundImage})`,
                                            backgroundSize: 'cover',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            borderBottomLeftRadius: '8px',
                                            borderBottomRightRadius: '8px',
                                        }}>
                                        <p style={{
                                            marginTop: '8px',
                                            color: '#4B5563',
                                            textAlign: 'center',
                                            maxWidth: '100%',
                                            wordWrap: 'break-word',
                                            fontFamily: 'Times New Roman, Times, serif',
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                        }}>
                                            {currentQuestion.text}
                                        </p>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <select
                                                className="hf-select"
                                                style={{
                                                    border: '2px solid black',
                                                    borderRadius: '10px',
                                                    padding: '5px',
                                                    width: '340px',
                                                    fontFamily: 'Times New Roman, Times, serif',
                                                    fontSize: '18px',
                                                    fontWeight: 'bold',
                                                }}
                                                value={selectedAnswer}
                                                onChange={(e) => handleAnswerSubmit(e.target.value)}
                                            >
                                                <option value="">Select an answer</option>
                                                {answers.map((answer) => (
                                                    <option key={answer.id} value={answer.text}>
                                                        {answer.text}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <div
                            className="hf-nav-btns"
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '16px',
                                marginTop: '56px',
                                marginBottom: '20px',
                            }}>
                            <button
                                className="hf-btn"
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: '10px 20px',
                                    width: '120px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                }}
                                onClick={handleBack}
                            >
                                Back
                            </button>
                            <button
                                className="hf-btn"
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: '10px 20px',
                                    width: '120px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                }}
                                onClick={goHome}
                            >
                                Home
                            </button>
                            <button
                                className="hf-btn"
                                style={{
                                    backgroundImage: `url(${buttonImage})`,
                                    backgroundSize: 'cover',
                                    border: '2px solid black',
                                    borderRadius: '10px',
                                    padding: '10px 20px',
                                    width: '120px',
                                    fontFamily: 'Times New Roman, Times, serif',
                                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                }}
                                onClick={handleSave}
                            >
                                Save
                            </button>
                        </div>
                        {showHelper && helperQuestion && (
                            <AnimatedHelper
                                isActive={showHelper}
                                onButtonClick={handleHelperClick}
                                buttons={helperButtons}
                                setButtons={setHelperButtons}
                                initialQuestionId={initialQuestionId}
                                onClose={() => setShowHelper(false)}
                                question={helperQuestion}
                                onAnimationComplete={() => {}}
                                onQuestionUpdate={(question) => setHelperQuestion(question)}
                            />
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default HealingFormulas;